import React, { useState, useEffect, useCallback } from 'react';
import { AdventureInvite, UserId } from 'coop-adventure-engine';
import adventureInvitesService from '../../services/adventureInvitesService';
import { AdventureInviteCard } from './AdventureInviteCard';
import apiService from '../../services/api';

export interface AdventureInviteListProps {
  /**
   * Optional click handler for adventure invites
   * If not provided, invites will use default join behavior (joinAdventureFromInvite)
   */
  onInviteClick?: (inviteId: string) => void;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Custom styles
   */
  style?: React.CSSProperties;
}

/**
 * Component for displaying list of adventure invites
 * All invites are clickable and will join the adventure when clicked
 */
export const AdventureInviteList: React.FC<AdventureInviteListProps> = ({
  onInviteClick,
  className = '',
  style
}) => {
  const [invites, setInvites] = useState<AdventureInvite[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Cache for inviter info (name and portrait)
  const [userInfoCache, setUserInfoCache] = useState<Map<UserId, { name: string; portraitId?: string }>>(new Map());

  const loadAdventureInvites = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const adventureInvites = await adventureInvitesService.getAdventureInvites();
      setInvites(adventureInvites);
      
      // Load user info for all inviters that we don't have cached
      setUserInfoCache((prevCache) => {
        const newCache = new Map(prevCache);
        const invitesToLoad = adventureInvites.filter(inv => !newCache.has(inv.inviterId));
        
        // Load user info in parallel
        Promise.all(
          invitesToLoad.map(async (invite) => {
            try {
              const userInfo = await apiService.getUserInfo(invite.inviterId);
              setUserInfoCache((currentCache) => {
                const updatedCache = new Map(currentCache);
                const cacheEntry: { name: string; portraitId?: string } = {
                  name: userInfo.playerName || userInfo.username || `User ${invite.inviterId.substring(0, 8)}`
                };
                if (userInfo.portraitId) {
                  cacheEntry.portraitId = userInfo.portraitId;
                }
                updatedCache.set(invite.inviterId, cacheEntry);
                return updatedCache;
              });
            } catch (err) {
              console.error(`Failed to load user info for ${invite.inviterId}:`, err);
              setUserInfoCache((currentCache) => {
                const updatedCache = new Map(currentCache);
                updatedCache.set(invite.inviterId, {
                  name: `User ${invite.inviterId.substring(0, 8)}`
                });
                return updatedCache;
              });
            }
          })
        );
        
        return newCache;
      });
    } catch (err) {
      console.error('Failed to load adventure invites:', err);
      setError(err instanceof Error ? err.message : 'Failed to load adventure invites');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAdventureInvites();

    // Listen for adventure invite updates
    const handleAdventureInviteUpdate = () => {
      loadAdventureInvites();
    };

    window.addEventListener('adventure_invite:update', handleAdventureInviteUpdate);
    return () => {
      window.removeEventListener('adventure_invite:update', handleAdventureInviteUpdate);
    };
  }, [loadAdventureInvites]);

  const handleInviteClick = useCallback(
    (inviteId: string) => {
      if (onInviteClick) {
        onInviteClick(inviteId);
      } else {
        // Default behavior: join the adventure from the invite
        adventureInvitesService.joinAdventureFromInvite(inviteId)
          .then(({ sessionId, playerId }) => {
            // Navigate to the session
            window.location.href = `/scene/${sessionId}/${playerId}`;
          })
          .catch((error) => {
            console.error('Failed to join adventure from invite:', error);
          });
      }
    },
    [onInviteClick]
  );

  if (isLoading) {
    return (
      <div
        className={`adventure-invite-list loading ${className}`}
        style={style}
        data-testid="adventure-invite-list-loading"
      >
        <div className="text-center py-4">
          <p className="text-gray-400 italic text-sm">Loading adventure invites...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`adventure-invite-list error ${className}`}
        style={style}
        data-testid="adventure-invite-list-error"
      >
        <div className="text-center py-4">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (invites.length === 0) {
    return (
      <div
        className={`adventure-invite-list empty ${className}`}
        style={style}
        data-testid="adventure-invite-list-empty"
      >
        <div className="flex items-center justify-center py-2" data-testid="empty-adventure-invites">
          <p className="italic text-center text-gray-400">No adventure invites</p>
        </div>
      </div>
    );
  }

  // All adventure invites are clickable - render as buttons
  return (
    <div
      className={`adventure-invite-list ${className}`}
      style={style}
      data-testid="adventure-invite-list"
    >
      <div
        className="adventure-invite-list-items flex flex-wrap justify-center"
        style={{ gap: '0.5rem' }}
        data-testid="adventure-invite-items-list"
      >
        {invites.map((invite) => {
          const userInfo = userInfoCache.get(invite.inviterId);
          const displayName = userInfo?.name || `User ${invite.inviterId.substring(0, 8)}`;
          
          return (
            <button
              key={invite.id}
              onClick={() => handleInviteClick(invite.id)}
              className="adventure-invite-item-button inventory-item-button"
              data-testid={`adventure-invite-${invite.id}`}
              data-invite-id={invite.id}
              aria-label={`Join adventure invite from ${displayName}`}
            >
              <AdventureInviteCard
                inviteId={invite.id}
                noFrame={true}
                className="adventure-invite-item-adventure-card"
              />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default AdventureInviteList;

